/**
 * The DM's latest reopen reason, for the completion box (owner ruling
 * 2026-10-22, round 3: stores kept completing tasks reopened for a missing
 * photo with no photo — show them what was asked; a file stays optional).
 * The reason rides on the 'reopened' timeline event ("Reopened the task:
 * <reason>", tasks.ts reopenTask). Pure; the text is UNTRUSTED (shown as
 * plain text, never HTML).
 */

/** The fields of a thread entry this reads (TaskThreadEntry, taskComments.ts). */
export interface ReopenSource {
  entryType: 'comment' | 'event';
  eventKind: string;
  body: string;
  authorName: string;
  authorUpn: string;
  createdAt: string;
}

export interface ReopenNote {
  /** Who reopened it (typed/display name, else the address). */
  by: string;
  /** When (ISO). */
  at: string;
  /** Their reason ('' on an old reopen recorded before reasons were asked). */
  reason: string;
  /** The store undid its OWN completion (B3) — "Completion undone", not a DM's reopen (UI review 3, S1). */
  undo?: boolean;
}

/**
 * A 'reopened' timeline row's body: "Reopened the task: <reason>" for a
 * DM / Auditor / Admin reopen, "Undid the completion: <reason>" for a store's
 * undo of its own completion (UI review 3, S1). The SERVER sets the prefix from
 * who is asking (api/src/writePolicy.ts) — a client can't make a reopen read
 * as an undo or the other way round. Pure (shared with api/).
 */
export const REOPEN_PREFIX = 'Reopened the task: ';
export const UNDO_PREFIX = 'Undid the completion: ';
const REOPEN_RE = /^\s*Reopened the task:\s*/i;
const UNDO_RE = /^\s*Undid the completion:\s*/i;

/** What a 'reopened' row's body says: an undo or not, and the reason ('' when the body has neither prefix). */
export function readReopenBody(body: string): { undo: boolean; reason: string } {
  if (UNDO_RE.test(body)) return { undo: true, reason: body.replace(UNDO_RE, '').trim() };
  if (REOPEN_RE.test(body)) return { undo: false, reason: body.replace(REOPEN_RE, '').trim() };
  return { undo: false, reason: '' };
}

/** The body for a reopen (`undo` = a store's own undo): any prefix the text already has is replaced. */
export function reopenBody(text: string, undo: boolean): string {
  const t = text.trim();
  const reason = UNDO_RE.test(t) || REOPEN_RE.test(t) ? readReopenBody(t).reason : t;
  return `${undo ? UNDO_PREFIX : REOPEN_PREFIX}${reason}`;
}

/** The most recent reopen event, or null when the task was never reopened. */
export function latestReopen(entries: readonly ReopenSource[]): ReopenNote | null {
  let latest: ReopenSource | null = null;
  for (const e of entries) {
    if (e.entryType !== 'event' || e.eventKind !== 'reopened') continue;
    if (!latest || e.createdAt.localeCompare(latest.createdAt) >= 0) latest = e;
  }
  if (!latest) return null;
  const { undo, reason } = readReopenBody(latest.body);
  return { by: latest.authorName.trim() || latest.authorUpn.trim() || 'someone', at: latest.createdAt, reason, ...(undo ? { undo } : {}) };
}

/**
 * Tasks.ReopenNote — the server's copy of the latest reopen (api/src/writeRun.ts
 * reopenNote; owner ruling 2026-10-25: the reason on every task card). JSON
 * {"v":1,"by","at","reason"}; anything else (blank, older rows, a hand edit) is
 * null. Plain text only.
 */
export function parseReopenNote(raw: unknown): ReopenNote | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const text = (x: unknown, max: number) => (typeof x === 'string' ? x.trim().slice(0, max) : '');
  const reason = text(o.reason, 500);
  const at = text(o.at, 40);
  if (o.v !== 1 || !reason || !Number.isFinite(Date.parse(at))) return null;
  return { by: text(o.by, 100) || 'someone', at, reason, ...(o.undo === true ? { undo: true } : {}) };
}
